import { useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { PodhiveLogo } from "@/components/taxpeerlogo.component";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import i18next from "i18next";

/**
 * Firebase Action Handler
 *
 * This is the main entry point for all Firebase email actions.
 * It reads the 'mode' parameter and routes to the appropriate handler:
 *
 * - resetPassword: Password reset flow
 * - verifyEmail: Email verification flow
 * - recoverEmail: Email change recovery flow
 *
 * Firebase will send users to this URL with ?mode=X&oobCode=Y
 *
 * Based on: https://firebase.google.com/docs/auth/custom-email-handler
 *
 * Configure in Firebase Console:
 * Authentication → Templates → Customize action URL
 * Set to: https://app.podhive.de/auth/action
 */
const ActionHandlerPage = () => {
	const { t } = useTranslation("common");
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();

	useEffect(() => {
		const mode = searchParams.get("mode");
		const oobCode = searchParams.get("oobCode");
		const continueUrl = searchParams.get("continueUrl");
		const lang = searchParams.get("lang");

		// Set language from Firebase email link if provided
		if (lang) {
			const normalizedLang = lang.startsWith("de") ? "de" : "en";
			if (i18next.language !== normalizedLang) {
				i18next.changeLanguage(normalizedLang);
			}
		}

		// Validate that we have the required parameters
		if (!mode || !oobCode) {
			console.error("❌ Missing mode or oobCode parameter");
			navigate("/auth/login");
			return;
		}

		// Check if we need to redirect to a different environment
		// This handles the case where Firebase action URL is set to staging,
		// but we want to support local development
		if (continueUrl) {
			// Simple check: if continueUrl contains "localhost" and we're not on localhost
			const isTargetLocalhost =
				continueUrl.includes("localhost") || continueUrl.includes("127.0.0.1");
			const isCurrentLocalhost = window.location.hostname === "localhost";

			if (isTargetLocalhost && !isCurrentLocalhost) {
				// We're on staging but should be on localhost - redirect
				const paramsWithoutContinue = new URLSearchParams(searchParams);
				paramsWithoutContinue.delete("continueUrl");

				const redirectUrl = `http://localhost:5173/auth/action?${paramsWithoutContinue.toString()}`;
				console.log(`🔄 Redirecting to localhost: ${redirectUrl}`);

				window.location.href = redirectUrl;
				return;
			}
		}

		// Route to the appropriate handler based on mode
		switch (mode) {
			case "resetPassword":
				navigate(`/auth/reset-password?${searchParams.toString()}`, {
					replace: true,
				});
				break;

			case "verifyEmail":
				navigate(`/auth/verify-email?${searchParams.toString()}`, {
					replace: true,
				});
				break;

			case "recoverEmail":
				// Future: Handle email recovery
				console.warn("⚠️ Email recovery not yet implemented");
				navigate("/auth/login");
				break;

			default:
				console.error(`❌ Unknown action mode: ${mode}`);
				navigate("/auth/login");
				break;
		}
	}, [searchParams, navigate]);

	// Show loading state while routing
	return (
		<div className="flex min-h-screen flex-col items-center justify-center bg-card p-4">
			<div className="w-full max-w-[400px] space-y-8 text-center">
				<div className="flex justify-center">
					<PodhiveLogo width={125} bildmarke={false} />
				</div>
				<div className="space-y-4">
					<Loader2 className="h-12 w-12 animate-spin mx-auto text-primary" />
					<p className="text-muted-foreground">
						{t("actionHandler.processing")}
					</p>
				</div>
			</div>
		</div>
	);
};

export default ActionHandlerPage;
